import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import IgnisIcon from '../components/icons/IgnisIcon';
import RiskDistributionBar from '../components/RiskDistributionBar';
import ZoneMap from '../components/briefing/ZoneMap';
import { useAuth } from '../contexts/AuthProvider';
import { useBriefingZones } from '../hooks/useBriefingZones';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import { bandKindOf, FLAGGED_SHARE_PCT } from '../lib/riskText';
import {
  RISK_PT,
  fmtDateTime,
  loadConcelhos,
  loadLiveFresh,
  type Concelho,
  type ConcelhoLive,
  type LiveDoc,
} from '../lib/concelhos';

// The web mirror of the daily email briefing (docs/modules/municipios/
// briefing-concelho-plan.md). Access is per email OR email domain, per zone:
// briefing_subscriptions x feature_grants('briefing'), resolved server-side by
// the my_briefing_zones RPC. The page self-gates like Simulação.
const CONTACT_EMAIL = 'institucional@tutela.land';
const MAILTO = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(
  'Pedido de acesso ao Briefing IGNIS',
)}`;

// Official IPMA/ICNF 5-level scale (Art. 43 DL 82/2021: theirs is THE index).
const RCM_PT: Record<number, string> = {
  1: 'Reduzido',
  2: 'Moderado',
  3: 'Elevado',
  4: 'Muito elevado',
  5: 'Máximo',
};
const rcmLabel = (level: number | null | undefined) =>
  level != null && RCM_PT[level] ? RCM_PT[level] : 'indisponível';

const TREND_PT: Record<string, string> = {
  sobe: '▲ sobe',
  desce: '▼ desce',
  'estável': '◆ estável',
};

const DISCLAIMER =
  'Este briefing é apoio à decisão e não substitui o índice oficial de perigo de incêndio ' +
  'rural (IPMA/ICNF, art. 43.º do DL 82/2021) nem os avisos da ANEPC. As pontuações Ignis ' +
  'são relativas (ordenação de risco), não probabilidades absolutas de incêndio.';

const DEGRADED_NOTE =
  'A publicação dos dados meteorológicos ERA5 (Copernicus) está atrasada e o mapa está em ' +
  'modo sazonal de recurso. As pontuações de hoje têm menor fiabilidade que o habitual; ' +
  'confirme sempre o índice oficial IPMA/ICNF.';

function RestrictedExplainer() {
  const { user } = useAuth();
  return (
    <div className="min-h-[calc(100vh-4rem)] bg-ink-950 text-mist">
      <div className="mx-auto max-w-3xl px-6 py-20">
        <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-ember/30 bg-ember/10 px-4 py-1.5 font-mono text-sm font-semibold text-ember">
          <IgnisIcon className="h-4 w-4" />
          IGNIS · Briefing
        </div>

        <h1 className="font-display text-3xl font-bold tracking-tight text-mist sm:text-4xl">
          Briefing diário de risco por concelho
        </h1>

        <p className="mt-6 text-lg leading-relaxed text-mist-muted">
          Um resumo operacional diário por concelho: risco Ignis a 1 km, tendência a 24 h,
          perigo oficial IPMA/ICNF e fogos recentes. Concebido para GTF e serviços municipais
          de proteção civil, entregue por email e nesta página.
        </p>

        <div className="mt-10 rounded-xl border border-ember/40 bg-ember/10 px-5 py-4">
          {user ? (
            <p className="text-sm leading-relaxed text-mist">
              A sua conta (<span className="font-mono text-ember">{user.email}</span>) ainda não
              tem zonas de briefing atribuídas. Solicite acesso abaixo.
            </p>
          ) : (
            <p className="text-sm leading-relaxed text-mist">
              O acesso ao briefing requer uma conta autorizada.{' '}
              <Link to="/entrar" className="font-semibold text-ember hover:underline">
                Entrar
              </Link>{' '}
              ou solicite acesso abaixo.
            </p>
          )}
        </div>

        <div className="mt-10 rounded-xl border border-ink-700 bg-ink-900 p-6">
          <h2 className="font-display text-xl font-bold text-mist">Como pedir acesso</h2>
          <p className="mt-3 leading-relaxed text-mist-muted">
            O acesso é atribuído por entidade (a todo o domínio de email institucional, p. ex.{' '}
            <span className="font-mono text-sm">cm-exemplo.pt</span>) ou a endereços
            específicos, e limitado aos concelhos contratados. Contacte-nos a partir de um
            endereço institucional, indicando a entidade e os concelhos pretendidos.
          </p>
          <a
            href={MAILTO}
            className="mt-5 inline-flex items-center gap-2.5 rounded-lg bg-ember px-6 py-3 font-semibold text-ink-950 shadow-[0_0_30px_-8px_#F26A4B] transition-all duration-200 hover:brightness-110 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ember"
          >
            Solicitar acesso
          </a>
          <p className="mt-4 font-mono text-sm text-mist-faint">{CONTACT_EMAIL}</p>
        </div>

        <p className="mt-10 text-sm text-mist-muted">
          É público em geral? As páginas de{' '}
          <Link to="/ignis/risco" className="font-medium text-ember hover:brightness-110">
            risco por concelho
          </Link>{' '}
          estão disponíveis para todos.
        </p>
      </div>
    </div>
  );
}

function ZoneCard({
  concelho,
  live,
  share = false,
}: {
  concelho: Concelho;
  live: ConcelhoLive | undefined;
  /** Share-banded map (rebuilt model): the per-concelho number is the share of
   * the concelho inside the 8 percent of the country the map flags (D9). */
  share?: boolean;
}) {
  if (!live) return null;
  const f = live.forecast24h;
  const d = live.delta;
  return (
    <div className="rounded-xl border border-ink-700 bg-ink-900 p-6">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="font-display text-xl font-bold text-mist">
          <Link to={`/ignis/risco/${concelho.slug}`} className="hover:text-ember">
            {concelho.nome}
          </Link>
        </h2>
        <span className="font-mono text-xs text-mist-faint">
          distrito de {concelho.distrito} · {live.n_cells} células de 1 km
        </span>
      </div>

      <div className="mt-4">
        <RiskDistributionBar risk={live.risk} total={live.n_cells} />
      </div>

      <ZoneMap concelho={concelho} />

      <div className="mt-4 grid gap-3 text-sm text-mist-muted sm:grid-cols-2">
        <div>
          {share
            ? `Área do concelho nos ${FLAGGED_SHARE_PCT}% do país com maior risco: `
            : 'Área em risco Moderado ou acima: '}
          <span className="font-semibold text-mist">{live.mod_plus_pct}%</span>
          {d && (
            <span className={d.mod_plus_pct > 0 ? 'text-ember' : 'text-mist-faint'}>
              {' '}
              ({d.mod_plus_pct > 0 ? '▲' : d.mod_plus_pct < 0 ? '▼' : '◆'}{' '}
              {d.mod_plus_pct > 0 ? '+' : ''}
              {d.mod_plus_pct} pp vs ontem)
            </span>
          )}
        </div>
        {live.anomaly_mean != null && (
          <div>
            Percentil sazonal (anomalia): médio{' '}
            <span className="font-semibold text-mist">{Math.round(live.anomaly_mean)}</span> /
            máximo{' '}
            <span className="font-semibold text-mist">{Math.round(live.anomaly_max ?? 0)}</span>{' '}
            <span className="text-mist-faint">(0-100)</span>
          </div>
        )}
        {f && (
          <div>
            Amanhã (24 h):{' '}
            <span className="font-semibold text-mist">{TREND_PT[f.trend] ?? f.trend}</span>{' '}
            <span className="text-mist-faint">
              (Moderado+ previsto {f.mod_plus_pct}%, dominante {RISK_PT[f.dominant]})
            </span>
          </div>
        )}
        {live.rcm && (
          <div>
            Perigo oficial IPMA/ICNF:{' '}
            <span className="font-semibold text-mist">hoje {rcmLabel(live.rcm.d0)}</span>,{' '}
            amanhã {rcmLabel(live.rcm.d1)}
          </div>
        )}
      </div>

      <div className="mt-4 border-t border-ink-700 pt-4 text-sm text-mist-muted">
        {live.fires_7d && live.fires_7d.count > 0 ? (
          <>
            <span className="font-semibold text-mist">
              {live.fires_7d.count} fogo{live.fires_7d.count === 1 ? '' : 's'} nos últimos 7
              dias
            </span>
            <ul className="mt-2 space-y-1 font-mono text-xs text-mist-faint">
              {live.fires_7d.items.map((fi, i) => (
                <li key={i}>
                  {fi.start_datetime?.slice(0, 16)}
                  {fi.burned_area_ha != null &&
                    fi.burned_area_ha >= 0.05 && // below this toFixed(1) shows "0.0"
                    ` · ${fi.burned_area_ha.toFixed(1)} ha`}
                  {fi.source && ` · ${fi.source}`}
                </li>
              ))}
            </ul>
          </>
        ) : (
          <span>Sem fogos registados no concelho nos últimos 7 dias.</span>
        )}
      </div>
    </div>
  );
}

function BriefingView({ zones }: { zones: string[] }) {
  const [live, setLive] = useState<LiveDoc | null>(null);
  const [bySlugStatic, setBySlugStatic] = useState<Map<string, Concelho> | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    Promise.all([loadLiveFresh(), loadConcelhos()])
      .then(([liveDoc, staticDoc]) => {
        if (cancelled) return;
        setLive(liveDoc);
        setBySlugStatic(new Map(staticDoc.concelhos.map((c) => [c.slug, c])));
      })
      .catch(() => !cancelled && setError(true));
    return () => {
      cancelled = true;
    };
  }, []);

  if (error) {
    return (
      <div className="mx-auto max-w-3xl px-6 py-20 text-mist-muted">
        Não foi possível carregar os dados do briefing. Tente novamente dentro de momentos.
      </div>
    );
  }
  if (!live || !bySlugStatic) {
    return (
      <div className="flex min-h-[calc(100vh-4rem)] items-center justify-center bg-ink-950">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-ink-700 border-t-ember" />
      </div>
    );
  }

  const liveBySlug = new Map(live.concelhos.map((c) => [c.slug, c]));

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-ink-950 text-mist">
      <div className="mx-auto max-w-3xl px-6 py-14">
        <div className="mb-2 inline-flex items-center gap-2 rounded-full border border-ember/30 bg-ember/10 px-4 py-1.5 font-mono text-sm font-semibold text-ember">
          <IgnisIcon className="h-4 w-4" />
          IGNIS · Briefing
        </div>
        <h1 className="font-display text-3xl font-bold tracking-tight">Briefing diário</h1>
        <p className="mt-2 font-mono text-sm text-mist-faint">
          {live.for_date} · previsão de {fmtDateTime(live.prediction_time)} ·{' '}
          {live.model_version}
        </p>

        {live.degraded && (
          <div className="mt-6 rounded-xl border border-amber-500/50 bg-amber-500/10 px-5 py-4 text-sm leading-relaxed text-mist">
            <span className="font-semibold">Atenção:</span> {DEGRADED_NOTE}
          </div>
        )}

        <div className="mt-8 space-y-6">
          {zones
            .filter((slug) => bySlugStatic.has(slug))
            .map((slug) => (
              <ZoneCard
                key={slug}
                concelho={bySlugStatic.get(slug)!}
                live={liveBySlug.get(slug)}
                share={bandKindOf(live.model_version) === 'share'}
              />
            ))}
        </div>

        <p className="mt-10 text-xs leading-relaxed text-mist-faint">
          {DISCLAIMER}{' '}
          <Link to="/ignis/validacao" className="text-ember hover:brightness-110">
            Validação pública
          </Link>
          .
        </p>
      </div>
    </div>
  );
}

export default function Briefing() {
  useDocumentMeta(
    'Briefing diário por concelho (acesso restrito) | Tutela IGNIS',
    'Briefing operacional diário de risco de incêndio por concelho para GTF e proteção civil municipal. Acesso reservado a entidades autorizadas.',
    undefined, // gated: no canonical, noindex
    { noindex: true },
  );

  const { zones, loading } = useBriefingZones();

  if (loading) {
    return (
      <div className="flex min-h-[calc(100vh-4rem)] items-center justify-center bg-ink-950">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-ink-700 border-t-ember" />
      </div>
    );
  }

  return zones && zones.length > 0 ? <BriefingView zones={zones} /> : <RestrictedExplainer />;
}
